import { styled } from "goober";
import { useEffect, useState } from "react";
import {
  isSqliteMediaType,
  mediaTypeFromFileName,
} from "../../../shared/attachmentPolicy";
import { type ArchiveEntry, streamArchive } from "../../lib/archive";
import { formatBytes } from "../../lib/formatBytes";
import { mediaTypeLabel } from "../../lib/mediaTypeLabel";
import { FilePreviewModal } from "../AttachmentHoverPreview";
import { CountBadge } from "../CountBadge";
import { Icon, type IconName } from "../Icon";
import {
  ChatpadBody,
  ChatpadList,
  ChatpadListBadge,
  ChatpadListHeader,
  ChatpadListRow,
  ChatpadListScrollArea,
} from "../PersonalChats/ChatpadList";
import { Text } from "../Text";
import { WavyLine } from "../WavyLine";
import { Binary } from "./Binary";
import { extensionFromMime } from "./download";
import type { FilePreviewTarget, FileRenderControl } from "./types";
import { useFileActions } from "./useFileActions";

const MAX_INLINE_BYTES = 25 * 1024 * 1024;

type Props = {
  url: string;
  contentType: string;
  fileName?: string;
  bleed?: number;
  onControls?: (controls: FileRenderControl[]) => void;
  onOpenFile?: (file: FilePreviewTarget) => void;
};

// Browsers can't fetch blob: URLs under our CSP, so an entry's bytes are
// previewed via a base64 data: URL instead.
function toBase64(bytes: Uint8Array): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

// Files without a recognized extension are still worth previewing as text when
// they have no NUL bytes (source files, configs, logs), so default those to
// text/plain rather than an undownloadable octet-stream.
function looksTextual(bytes: Uint8Array): boolean {
  const n = Math.min(bytes.length, 1024);
  for (let i = 0; i < n; i++) if (bytes[i] === 0) return false;
  return n > 0;
}

// Cheap per-entry icon by type; avoids decoding entries just to list them.
function iconForMediaType(mediaType: string): IconName {
  if (isSqliteMediaType(mediaType)) return "database";
  if (mediaType === "text/csv") return "table";
  if (mediaType === "application/pdf") return "file-scan";
  if (mediaType.startsWith("text/") || mediaType === "application/json")
    return "file-braces-corner";
  return "paperclip";
}

const Center = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-secondary);
`;

type LoadState =
  | { status: "loading" }
  | { status: "too-large" }
  | { status: "error"; message: string }
  | { status: "ready"; entries: ArchiveEntry[] };

function useArchive(
  url: string,
  contentType: string,
  fileName?: string
): LoadState {
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    // oxlint-disable-next-line react/set-state-in-effect -- EffectSetState: resets to loading at the top of the AbortController archive fetch
    setState({ status: "loading" });

    void (async () => {
      try {
        const res = await fetch(url, { credentials: "include", signal });
        if (!res.ok) {
          setState({ status: "error", message: `HTTP ${res.status}` });
          return;
        }
        if (Number(res.headers.get("Content-Length")) > MAX_INLINE_BYTES) {
          res.body?.cancel();
          setState({ status: "too-large" });
          return;
        }
        if (!res.body) {
          setState({ status: "error", message: "Empty response" });
          return;
        }
        const final = await streamArchive(
          res.body,
          contentType,
          fileName,
          (entries) => {
            if (!signal.aborted && entries.length) {
              setState({ status: "ready", entries });
            }
          }
        );
        if (!signal.aborted) setState({ status: "ready", entries: final });
      } catch (err) {
        if (signal.aborted) return;
        setState({
          status: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();

    return () => controller.abort();
  }, [url, contentType, fileName]);

  return state;
}

type Preview = {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  url: string;
};

export function Archive({
  url,
  contentType,
  fileName,
  bleed,
  onControls,
  onOpenFile,
}: Props) {
  const state = useArchive(url, contentType, fileName);
  const [preview, setPreview] = useState<Preview | null>(null);

  useFileActions({
    url,
    fileName: fileName ?? `archive.${extensionFromMime(contentType)}`,
    onControls,
    controlId: "download-archive",
    label: "Download archive",
  });

  // Decode only the clicked entry, even for archives with thousands of files.
  const open = async (entry: ArchiveEntry) => {
    const bytes = await entry.extract();
    let mediaType = mediaTypeFromFileName(entry.path);
    if (mediaType === "application/octet-stream" && looksTextual(bytes)) {
      mediaType = "text/plain";
    }
    const file = {
      fileName: entry.path,
      mediaType,
      sizeBytes: entry.size,
      url: `data:${mediaType};base64,${toBase64(bytes)}`,
    };
    if (onOpenFile) onOpenFile(file);
    else setPreview(file);
  };

  if (state.status === "loading") {
    return (
      <Center>
        <WavyLine size={24} opacity={0.4} />
      </Center>
    );
  }
  if (state.status === "too-large") {
    return (
      <Binary
        url={url}
        contentType={contentType}
        fileName={fileName}
        hideActions={!!onControls}
      />
    );
  }
  if (state.status === "error") {
    return (
      <Center>
        <Text variant="text-small">
          Unable to read archive: {state.message}
        </Text>
      </Center>
    );
  }
  if (state.entries.length === 0) {
    return (
      <Center>
        <Text variant="text-small">Empty archive</Text>
      </Center>
    );
  }

  return (
    <>
      <ChatpadBody
        data-fill="true"
        style={
          bleed
            ? { margin: -bleed, height: `calc(100% + ${bleed * 2}px)` }
            : undefined
        }
      >
        <ChatpadList
          role="region"
          aria-label="Files in archive"
          data-fill="true"
        >
          <ChatpadListHeader style={{ paddingInline: 16, gap: 8 }}>
            <Text variant="text-small-md">Files</Text>
            <CountBadge>
              <Text variant="text-smaller-md">{state.entries.length}</Text>
            </CountBadge>
          </ChatpadListHeader>
          <ChatpadListScrollArea>
            {state.entries.map((entry) => (
              <ChatpadListRow
                key={entry.path}
                title={entry.path}
                description={`${mediaTypeLabel(mediaTypeFromFileName(entry.path))} · ${formatBytes(entry.size)}`}
                badge={
                  <ChatpadListBadge>
                    <Icon
                      name={iconForMediaType(mediaTypeFromFileName(entry.path))}
                      size={16}
                    />
                  </ChatpadListBadge>
                }
                onPress={() => void open(entry)}
              />
            ))}
          </ChatpadListScrollArea>
        </ChatpadList>
      </ChatpadBody>
      <FilePreviewModal
        open={preview != null}
        onClose={() => setPreview(null)}
        fileName={preview?.fileName ?? ""}
        mediaType={preview?.mediaType ?? ""}
        sizeBytes={preview?.sizeBytes ?? 0}
        url={preview?.url ?? ""}
      />
    </>
  );
}
